import { EditTask } from "../../components";
import { useState, useCallback } from "react";
import { updateTask, deleteTask } from "../../utilities/Database.mjs";
import { attachItemCounter } from "./attachItemCounter";

// Shared task editing UI. Pass in the item the task belongs to and callbacks for when the task has been edited or deleted.
// Optional `taskListModel`: in-memory list to splice on save/delete (e.g. task bucket page). Defaults to `item.tasks?.items`.
// Returns a function to call to show the edit task popup and the popup itself.
export const useEditTaskPopup = ({ item, taskEdited, taskDeleted, taskListModel }, showNow = false) => {
  const [showEditTask, setShowEditTask] = useState(showNow);
  const [editTask, setEditTask] = useState(null);

  // Passed to EditTask.  When the Save button is pressed, this is called.
  // throws on errors.
  const modelForList = taskListModel ?? item?.tasks?.items;

  const saveEditedTask = useCallback(async (task) => {
    task.id = editTask.id;
    task.itemId = item.id;

    // Update the database and the in-memory task list on the item, so the
    // parent's recomputed task buckets pick up the new field values
    // (otherwise e.g. a renamed task keeps showing its old name until the
    // page is reloaded). Passing `item.tasks.items` makes `updateTask`
    // splice the saved task into the array in-place. This can throw, caller
    // catches and shows error...
    let savedTask = await updateTask(task, item, modelForList);

    // When I save the task to the database it strips out the itemCounter field
    // I read at load time -- resolve it back onto the saved task.
    await attachItemCounter(savedTask, editTask, item);

    // Call the parent to update the task list UI. Note: this runs per
    // autosave flush, so it must NOT close the modal -- the user may still
    // be editing.
    taskEdited(savedTask);
  }, [editTask, item, taskEdited, modelForList]);

  // Passed to EditTask, deletes the given taskId
  const onDeleteTask = useCallback(async(id) => {
    // Update the database and in-memory list. Can throw, caller catches and shows error.
    await deleteTask(id, modelForList);

    // Update UI
    setShowEditTask(false);
    taskDeleted(id);
  }, [taskDeleted, modelForList]);

  const showEditTaskPopup = useCallback((task) => {
    setEditTask(task);
    setShowEditTask(true);
  }, []);
  
  const TaskEditor = <EditTask
    show={showEditTask}
    onHide={() => setShowEditTask(false)}
    task={editTask}
    item={item}
    save={saveEditedTask}
    delete={onDeleteTask}
  />

  return { showEditTaskPopup, TaskEditor }
}